import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { instagramService } from '../services/instagramService';
import {
    instagramProfiles,
    publicInstagramPosts,
    savedInstagramAccounts,
    supabase
} from '../lib/supabase';
import type { InstagramProfile } from '../lib/supabase';
import { profileLookupErrorMessage } from '../lib/functionErrorMessage';
import {
    transformSavedAccountToUnified,
    transformUnifiedToInstagramProfile
} from '../utils/instagramDataTransform';

export interface InstagramState {
    // 상태
    connectedProfile: InstagramProfile | null;
    isConnected: boolean;
    isLoading: boolean;
    isConnecting: boolean;
    error: string | null;

    // 연결 플로우 상태
    authCode: string | null;
    accessToken: string | null;
}

export interface InstagramActions {
    // 상태 관리
    setConnectedProfile: (profile: InstagramProfile | null) => void;
    setLoading: (loading: boolean) => void;
    setConnecting: (connecting: boolean) => void;
    setError: (error: string | null) => void;
    clearError: () => void;

    // Instagram 연결 액션
    initializeConnection: (userId: string) => Promise<void>;
    connectInstagram: (userId: string, authCode: string) => Promise<InstagramProfile>;
    connectByUsername: (userId: string, instagramUsername: string) => Promise<InstagramProfile>;
    checkConnection: (userId: string) => Promise<boolean>;
    disconnectInstagram: (userId: string) => Promise<void>;
    refreshProfile: (userId: string) => Promise<void>;
    triggerEdgeFunction: (userId: string, profileId: string) => Promise<void>;

    // 유틸리티
    generateAuthUrl: () => string;
    reset: () => void;
}

export const useInstagramStore = create<InstagramState & InstagramActions>()(
  persist(
    (set, get) => ({
    // 초기 상태
    connectedProfile: null,
    isConnected: false,
    isLoading: false,
    isConnecting: false,
    error: null,
    authCode: null,
    accessToken: null,

    // 상태 설정 함수들
    setConnectedProfile: (profile) => set({
        connectedProfile: profile,
        isConnected: !!profile
    }),
    setLoading: (isLoading) => set({ isLoading }),
    setConnecting: (isConnecting) => set({ isConnecting }),
    setError: (error) => set({ error }),
    clearError: () => set({ error: null }),

    // Instagram 연결 상태 초기화 - 저장된 계정에서 불러오기
    initializeConnection: async (userId: string) => {
        console.log(`🔍 initializeConnection 시작 - UserId: ${userId}`);

        // 이미 로딩 중이면 중복 실행 방지
        if (get().isLoading) {
            console.log(`⏳ 이미 초기화 중 - 중복 실행 방지`);
            return;
        }

        // userId 유효성 검증
        if (!userId || userId.trim() === '') {
            console.error('❌ 유효하지 않은 사용자 ID');
            set({
                error: '사용자 인증 정보가 유효하지 않습니다.',
                connectedProfile: null,
                isConnected: false,
                isLoading: false
            });
            return;
        }

        // 로딩 상태만 설정 (기존 데이터가 있을 수 있으므로 보존)
        console.log(`🔄 초기화 시작 - 기존 데이터 확인 중`);
        
        // 현재 상태 확인
        const currentState = get();
        
        // 이미 연결된 상태이고 같은 사용자인 경우 스킵
        if (currentState.isConnected && currentState.connectedProfile?.user_id === userId) {
            console.log(`✅ 이미 연결됨 - 동일 사용자: ${currentState.connectedProfile.username}`);
            set({ isLoading: false });
            return;
        }
        
        // 초기화 진행
        set({
            isLoading: true,
            error: null
        });

        // 🔥 다른 사용자 캐시 정리
        try {
            const { clearOtherUsersInstagramCache } = await import('../utils/instagramCacheManager');
            await clearOtherUsersInstagramCache(userId);
        } catch (error) {
            console.warn('⚠️ 캐시 정리 중 경고:', error);
        }

        try {
            // 사용자 본인의 계정만 조회 (연결된 계정)
            console.log(`📂 사용자 (${userId})의 본인 계정 조회 중...`);
            const userOwnedAccounts = await savedInstagramAccounts.getUserOwnedAccounts(userId);
            console.log(`📂 본인 계정 조회 결과:`, userOwnedAccounts?.length || 0, '개');

            // 본인 계정 검증 및 처리
            if (userOwnedAccounts && userOwnedAccounts.length > 0) {
                // 사용자 본인 계정만 조회했으므로 추가 필터링 불필요
                console.log(`✅ 사용자 본인 계정 확인됨:`, userOwnedAccounts.length, '개');

                const userAccounts = userOwnedAccounts;

                if (userAccounts.length > 0) {
                    // 가장 최근에 연결된 계정 사용
                    const latestAccount = userAccounts.sort((a, b) =>
                        new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
                    )[0];

                    console.log(`📋 최신 계정 정보:`, {
                        username: latestAccount.username,
                        user_id: latestAccount.user_id,
                        created: latestAccount.created_at,
                        updated: latestAccount.updated_at
                    });

                    // 계정 소유자 재확인
                    if (latestAccount.user_id !== userId) {
                        console.error(`❌ 계정 소유자 불일치: ${latestAccount.user_id} !== ${userId}`);
                        
                        // 🔥 불일치 시 캐시 완전 정리
                        const { clearUserInstagramData } = await import('../utils/instagramCacheManager');
                        await clearUserInstagramData(userId);
                        
                        throw new Error('계정 소유자가 일치하지 않습니다.');
                    }

                    // 통일된 데이터 변환 로직 사용
                    try {
                        const unifiedAccount = transformSavedAccountToUnified(latestAccount);
                        const convertedProfile = transformUnifiedToInstagramProfile(unifiedAccount);

                        console.log('✅ [Store] Instagram 프로필 연결 완료:', convertedProfile.username, {
                            user_id: convertedProfile.user_id,
                            followers: convertedProfile.followers_count,
                            following: convertedProfile.following_count,
                            posts: convertedProfile.posts_count,
                            profilePic: !!convertedProfile.profile_picture_url,
                            imagekitOptimized: unifiedAccount.is_imagekit_optimized ? '✅' : '❌',
                            lastUpdate: unifiedAccount.last_synced_at
                        });

                        set({
                            connectedProfile: convertedProfile,
                            isConnected: true,
                            isLoading: false,
                            error: null
                        });

                    } catch (transformError) {
                        console.error('❌ [Store] 데이터 변환 실패:', transformError);
                        throw new Error('계정 데이터 변환 중 오류가 발생했습니다.');
                    }
                } else {
                    console.log('📱 현재 사용자의 Instagram 프로필 없음 - 연결 플로우 표시 필요');
                    set({
                        connectedProfile: null,
                        isConnected: false,
                        isLoading: false,
                        error: null
                    });
                }
            } else {
                console.log('📱 Instagram 프로필 없음 - 연결 플로우 표시 필요');
                set({
                    connectedProfile: null,
                    isConnected: false,
                    isLoading: false,
                    error: null
                });
            }
        } catch (error: any) {
            console.error('❌ Instagram 연결 상태 확인 실패:', error);
            set({
                error: 'Instagram 연결 상태를 확인할 수 없습니다.',
                connectedProfile: null,
                isConnected: false,
                isLoading: false
            });
        } finally {
            // 안전장치: 로딩 상태 확실히 해제
            const currentState = get();
            if (currentState.isLoading) {
                console.log(`🔧 finally - 로딩 상태 강제 해제`);
                set({ isLoading: false });
            }
        }

        const finalState = get();
        console.log(`🎯 initializeConnection 완료 - isConnected: ${finalState.isConnected}, isLoading: ${finalState.isLoading}, user_id: ${userId}`);
    },

    // Instagram 계정 연결 (OAuth 방식)
    connectInstagram: async (userId: string, authCode: string): Promise<InstagramProfile> => {
        set({ isConnecting: true, error: null });

        try {
            // 1. Authorization Code를 Access Token으로 교환
            const accessToken = await instagramService.exchangeCodeForToken(authCode);

            // 2. Instagram 프로필 정보 가져오기
            const instagramData = await instagramService.getProfile(accessToken);

            // 3. Supabase에 프로필 저장
            const savedProfile = await instagramService.saveProfileToDatabase(
                userId,
                instagramData,
                accessToken
            );

            set({
                connectedProfile: savedProfile,
                isConnected: true,
                accessToken: accessToken,
                authCode: authCode
            });

            // 4. Edge Function 트리거 (방문자 추적 설정)
            await get().triggerEdgeFunction(userId, savedProfile.id);

            return savedProfile;
        } catch (error: any) {
            const errorMessage = getInstagramErrorMessage(error);
            set({ error: errorMessage });
            throw error;
        } finally {
            set({ isConnecting: false });
        }
    },

    // Instagram 계정 연결 (사용자 본인 계정만) - Edge Function과 완전 연동
    connectByUsername: async (userId: string, instagramUsername: string): Promise<InstagramProfile> => {
        set({ isConnecting: true, error: null });

        try {
            console.log(`🚀 내 Instagram 계정 연결 시작: ${instagramUsername}`);
            console.log(`⚠️ 주의: 본인의 Instagram 계정만 연결하세요!`);

            // Supabase 클라이언트 안전성 검증
            validateSupabaseClient();

            // 1. Edge Function을 통해 사용자 본인의 Instagram 데이터 수집 및 저장
            const { data, error } = await supabase.functions.invoke('instagram-profile', {
                body: {
                    instagramIDvalue: instagramUsername.replace('@', ''), // @ 제거
                    isUserOwned: true, // 사용자 본인 계정임을 명시
                    accountType: 'user_owned' // 계정 타입 명시
                }
            });

            if (error) {
                // 영어 오류 문구 대신 사유별 한국어 안내 (2026-10-28 대표 승인)
                throw new Error(await profileLookupErrorMessage(error, instagramUsername.replace('@', '')));
            }

            if (!data || !data.profile_data) {
                throw new Error(data?.error || 'Instagram 프로필을 찾을 수 없습니다.');
            }

            const fullInstagramData = data.profile_data;

            console.log(`✅ Edge Function 데이터 수집 완료:`, {
                username: fullInstagramData.username,
                followers: fullInstagramData.followersCount,
                posts: fullInstagramData.latestPosts?.length || 0,
                profilePic: !!fullInstagramData.profilePicUrl,
                hdProfilePic: !!fullInstagramData.profilePicUrlHD
            });

            // 2. Edge Function에서 저장된 데이터를 saved_instagram_accounts에서 불러오기
            const actualUsername = fullInstagramData.username || instagramUsername;

            // 약간의 지연을 두고 저장된 데이터 확인
            let savedAccount = null;
            let retryCount = 0;
            const maxRetries = 3;

            while (!savedAccount && retryCount < maxRetries) {
                if (retryCount > 0) {
                    await new Promise(resolve => setTimeout(resolve, 1000)); // 1초 대기
                }

                savedAccount = await savedInstagramAccounts.getByUsername(userId, actualUsername);
                retryCount++;

                if (savedAccount) {
                    console.log(`✅ 저장된 계정 데이터 확인 완료 (${retryCount}번째 시도)`);
                } else if (retryCount < maxRetries) {
                    console.log(`⏳ 저장된 계정 데이터 확인 중... (${retryCount}/${maxRetries})`);
                }
            }

            if (!savedAccount) {
                throw new Error('Edge Function에서 저장된 데이터를 찾을 수 없습니다.');
            }

            // 3. 통일된 데이터 변환 로직 사용
            let unifiedAccount;
            let convertedProfile;

            try {
                unifiedAccount = transformSavedAccountToUnified(savedAccount);
                convertedProfile = transformUnifiedToInstagramProfile(unifiedAccount);

                console.log(`✅ [Store] 프로필 변환 완료: ${convertedProfile.username}`);

                // 4. 상태 업데이트 - 통일된 변환 로직으로 완전한 데이터 제공
                set({
                    connectedProfile: convertedProfile,
                    isConnected: true
                });

                console.log(`🎉 [Store] Instagram 연결 완료! 통일된 데이터 활용:`, {
                    profile: '✅ 저장됨',
                    posts: `✅ ${unifiedAccount.latest_posts?.length || 0}개 저장됨`,
                    images: '✅ 최적화됨',
                    database: '✅ 다중 테이블 저장 완료',
                    profilePic: convertedProfile.profile_picture_url ? '✅' : '❌',
                    followers: convertedProfile.followers_count,
                    imagekitOptimized: unifiedAccount.is_imagekit_optimized ? '✅' : '❌'
                });

            } catch (transformError) {
                console.error('❌ [Store] connectByUsername 데이터 변환 실패:', transformError);
                throw new Error('계정 데이터 변환 중 오류가 발생했습니다.');
            }

            // 5. Edge Function 트리거 (방문자 추적 설정) - 선택사항
            try {
                await get().triggerEdgeFunction(userId, convertedProfile.id);
            } catch (triggerError) {
                console.warn('방문자 추적 설정 실패:', triggerError);
                // 이 에러는 무시하고 계속 진행
            }

            return convertedProfile;
        } catch (error: any) {
            console.error(`❌ Instagram 연결 실패:`, error);

            // 상세한 에러 분석
            if (error.message?.includes('Supabase')) {
                console.error('🔧 Supabase 관련 에러:', {
                    message: error.message,
                    hasSupabase: !!supabase,
                    hasFunctions: !!(supabase && supabase.functions),
                    supabaseUrl: import.meta.env.VITE_SUPABASE_URL ? '✅' : '❌',
                    supabaseKey: import.meta.env.VITE_SUPABASE_ANON_KEY ? '✅' : '❌'
                });
            }

            const errorMessage = getInstagramErrorMessage(error);
            set({ error: errorMessage });
            throw error;
        } finally {
            set({ isConnecting: false });
        }
    },

    // 연결 상태 확인 - 저장된 계정에서 확인
    checkConnection: async (userId: string): Promise<boolean> => {
        try {
            // userId 유효성 검증
            if (!userId || userId.trim() === '') {
                console.error('❌ checkConnection: 유효하지 않은 사용자 ID');
                return false;
            }

            console.log(`🔍 연결 상태 확인 - 사용자 본인 계정 (${userId})`);
            const userOwnedAccounts = await savedInstagramAccounts.getUserOwnedAccounts(userId);

            // 사용자 본인 계정만 가져왔으므로 추가 필터링 불필요
            const userAccounts = userOwnedAccounts || [];
            const isConnected = userAccounts.length > 0;

            console.log(`📂 연결 상태 확인 결과: ${isConnected ? '연결됨' : '연결 안됨'} (${userAccounts.length}개 계정)`);

            if (isConnected) {
                const latestAccount = userAccounts.sort((a, b) =>
                    new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
                )[0];

                // 계정 소유자 재확인
                if (latestAccount.user_id !== userId) {
                    console.error(`❌ 계정 소유자 불일치: ${latestAccount.user_id} !== ${userId}`);
                    return false;
                }

                // 통일된 데이터 변환 로직 사용
                try {
                    const unifiedAccount = transformSavedAccountToUnified(latestAccount);
                    const convertedProfile = transformUnifiedToInstagramProfile(unifiedAccount);

                    console.log(`✅ [Store] 연결된 계정 확인:`, {
                        username: convertedProfile.username,
                        user_id: convertedProfile.user_id,
                        followers: convertedProfile.followers_count,
                        imagekitOptimized: unifiedAccount.is_imagekit_optimized ? '✅' : '❌'
                    });

                    set({
                        connectedProfile: convertedProfile,
                        isConnected: true
                    });

                } catch (transformError) {
                    console.error('❌ [Store] checkConnection 데이터 변환 실패:', transformError);
                    set({
                        connectedProfile: null,
                        isConnected: false
                    });
                    return false;
                }
            } else {
                set({
                    connectedProfile: null,
                    isConnected: false
                });
            }

            return isConnected;
        } catch (error) {
            console.error('연결 상태 확인 실패:', error);
            set({
                connectedProfile: null,
                isConnected: false
            });
            return false;
        }
    },

    // Instagram 연결 해제 - 완전한 데이터 정리
    disconnectInstagram: async (userId: string) => {
        console.log(`🗑️ [Store] Instagram 연결 해제 시작: ${userId}`);
        set({ isLoading: true, error: null });

        try {
            // 1. 데이터베이스에서 모든 관련 데이터 삭제
            console.log(`🗄️ [Store] 데이터베이스 연결 해제 시작...`);
            await instagramService.disconnectInstagram(userId);

            // 2. Zustand 상태 완전 초기화
            console.log(`🔄 [Store] 상태 초기화 중...`);
            set({
                connectedProfile: null,
                isConnected: false,
                accessToken: null,
                authCode: null,
                error: null,
                isLoading: false,
                isConnecting: false
            });

            // 3. 🔥 새로운 캐시 매니저로 완전 정리
            console.log(`🧹 [Store] 캐시 매니저로 완전 정리 중...`);
            try {
                const { clearUserInstagramData } = await import('../utils/instagramCacheManager');
                await clearUserInstagramData(userId);
                console.log(`✅ [Store] 캐시 매니저 정리 완료`);
            } catch (cacheError) {
                console.warn('⚠️ [Store] 캐시 정리 중 경고:', cacheError);
            }

            // 4. 상태 재확인 및 강제 업데이트
            console.log(`🔍 [Store] 연결 해제 후 상태 재확인...`);
            const finalState = get();

            if (finalState.isConnected || finalState.connectedProfile) {
                console.warn(`⚠️ [Store] 상태가 완전히 정리되지 않음, 강제 초기화`);
                set({
                    connectedProfile: null,
                    isConnected: false,
                    accessToken: null,
                    authCode: null,
                    error: null,
                    isLoading: false,
                    isConnecting: false
                });
            }

            console.log(`🎉 [Store] Instagram 계정 연결 해제 완료!`);
            console.log(`📊 [Store] 최종 상태:`, {
                isConnected: get().isConnected,
                hasProfile: !!get().connectedProfile,
                hasToken: !!get().accessToken,
                isLoading: get().isLoading
            });

        } catch (error: any) {
            console.error('❌ [Store] Instagram 연결 해제 실패:', error);
            const errorMessage = getInstagramErrorMessage(error);
            set({
                error: errorMessage,
                isLoading: false
            });
            throw error;
        } finally {
            // 안전장치: 로딩 상태 확실히 해제
            set({ isLoading: false });
        }
    },

    // 프로필 정보 새로고침
    refreshProfile: async (userId: string) => {
        const { connectedProfile } = get();
        if (!connectedProfile) return;

        set({ isLoading: true, error: null });

        try {
            await instagramService.updateProfileStats(connectedProfile.id);

            // 업데이트된 프로필 정보 다시 가져오기
            const updatedProfile = await instagramProfiles.getById(connectedProfile.id);
            if (updatedProfile) {
                set({ connectedProfile: updatedProfile });
            }
        } catch (error: any) {
            const errorMessage = getInstagramErrorMessage(error);
            set({ error: errorMessage });
        } finally {
            set({ isLoading: false });
        }
    },

    // Instagram 인증 URL 생성
    generateAuthUrl: () => {
        return instagramService.generateAuthUrl();
    },

    // Edge Function 트리거
    triggerEdgeFunction: async (userId: string, profileId: string) => {
        try {
            // Supabase 클라이언트 안전성 검증
            validateSupabaseClient();

            // Supabase Edge Function 호출
            const { data, error } = await supabase.functions.invoke('instagram-connection-handler', {
                body: {
                    user_id: userId,
                    profile_id: profileId,
                    action: 'connect',
                    timestamp: new Date().toISOString()
                }
            });

            if (error) {
                console.error('Edge Function 호출 실패:', error);
            }
        } catch (error) {
            console.error('Edge Function 트리거 에러:', error);
        }
    },

    // 상태 초기화
    reset: () => set({
        connectedProfile: null,
        isConnected: false,
        isLoading: false,
        isConnecting: false,
        error: null,
        authCode: null,
        accessToken: null
    })
    }),
    {
      name: 'instagram-store',
      partialize: (state) => ({
        connectedProfile: state.connectedProfile,
        isConnected: state.isConnected
      }),
      storage: {
        getItem: (name) => {
          const str = localStorage.getItem(name);
          if (!str) return null;
          try {
            return JSON.parse(str);
          } catch {
            return null;
          }
        },
        setItem: (name, value) => {
          localStorage.setItem(name, JSON.stringify(value));
        },
        removeItem: (name) => {
          localStorage.removeItem(name);
        }
      }
    }
  )
);

// Instagram 에러 메시지 변환 함수
function getInstagramErrorMessage(error: any): string {
    if (typeof error === 'string') return error;

    if (error.message) {
        if (error.message.includes('Invalid authorization code')) {
            return 'Instagram 인증 코드가 유효하지 않습니다. 다시 시도해주세요.';
        }
        if (error.message.includes('Access token expired')) {
            return 'Instagram 액세스 토큰이 만료되었습니다. 다시 연결해주세요.';
        }
        if (error.message.includes('Rate limit exceeded')) {
            return 'Instagram API 요청 한도를 초과했습니다. 잠시 후 다시 시도해주세요.';
        }
        if (error.message.includes('User not found')) {
            return 'Instagram 사용자를 찾을 수 없습니다.';
        }
        if (error.message.includes('Permission denied')) {
            return 'Instagram 계정 접근 권한이 없습니다.';
        }
        if (error.message.includes('network')) {
            return '네트워크 연결을 확인해주세요.';
        }
        return error.message;
    }

    return 'Instagram 연결 중 오류가 발생했습니다.';
}

// Supabase 클라이언트 안전성 검증 함수
const validateSupabaseClient = () => {
    if (!supabase) {
        throw new Error('Supabase 클라이언트가 초기화되지 않았습니다.');
    }
    if (!supabase.functions) {
        throw new Error('Supabase Functions가 사용할 수 없습니다. 환경설정을 확인해주세요.');
    }
    return true;
}; 